/**
 * A tela «Uso dos recursos» (21/10/2026): o catálogo do que se conta e as contas
 * da tela, fora do `.tsx` para o `node --test` alcançar.
 *
 * As chaves de `USAGE_FEATURES` são as mesmas de `RECURSOS` em
 * `backend/app/services/feature_usage.py` — o teste do backend compara as duas
 * listas. O backend descarta chave desconhecida, então uma chave nova só conta
 * depois de entrar nos dois lados.
 */
import type { PlanName, UsagePanel, UsagePanelFeature, UsagePlan, UsageScope, UsageSurface } from "./api";
import type { ActiveTool } from "../state/toolStore";
import type { ProInviteTool } from "./viewerProInvite";

export type UsageGroup = "nav" | "cor" | "med" | "lei" | "com" | "pai" | "cmp" | "ali";

export const USAGE_GROUP_LABELS: Record<UsageGroup, string> = {
  nav: "Navegação",
  cor: "Cor e tinta",
  med: "Medição",
  lei: "Leitura",
  com: "Comentários",
  pai: "Painéis",
  cmp: "Comparação",
  ali: "Alinhamento",
};

export interface UsageFeatureDef {
  key: string;
  label: string;
  group: UsageGroup;
  /** Onde o botão existe. */
  scopes: readonly UsageScope[];
  /** Só com sessão (gerenciador). */
  sessionOnly?: boolean;
}

const AMBOS = ["viewer", "compare"] as const;
const VIEWER = ["viewer"] as const;
const COMPARE = ["compare"] as const;

export const USAGE_FEATURES = [
  { key: "mao", label: "Mão", group: "nav", scopes: AMBOS },
  { key: "zoom", label: "Zoom", group: "nav", scopes: AMBOS },
  { key: "rotacionar", label: "Rotacionar", group: "nav", scopes: AMBOS },
  { key: "espelhar", label: "Espelhar", group: "nav", scopes: AMBOS },
  { key: "ajustar", label: "Ajustar página", group: "nav", scopes: AMBOS },
  { key: "regua", label: "Régua", group: "med", scopes: AMBOS },
  { key: "area", label: "Medir área", group: "med", scopes: AMBOS },
  { key: "ler_codigo", label: "Ler Código", group: "lei", scopes: AMBOS },
  { key: "ler_braile", label: "Ler Braile", group: "lei", scopes: AMBOS },
  { key: "ler_texto", label: "Ler Texto", group: "lei", scopes: AMBOS },
  { key: "ler_tabela", label: "Ler Tabela", group: "lei", scopes: AMBOS },
  { key: "modo_texto", label: "Modo Texto", group: "lei", scopes: VIEWER },
  { key: "comparar_texto", label: "Comparar Texto", group: "lei", scopes: AMBOS },
  { key: "medir_texto", label: "Medir Texto", group: "med", scopes: AMBOS },
  { key: "reticula", label: "Medir Retícula", group: "med", scopes: AMBOS },
  { key: "conta_gotas", label: "Conta Gotas", group: "cor", scopes: AMBOS },
  { key: "comentarios", label: "Comentários", group: "com", scopes: AMBOS },
  { key: "nota_ponto", label: "Nota de ponto", group: "com", scopes: AMBOS },
  { key: "nota_selecao", label: "Nota de seleção", group: "com", scopes: AMBOS },
  { key: "tema", label: "Claro/Escuro", group: "pai", scopes: AMBOS },
  { key: "exportar_jpeg", label: "Exportar JPEG", group: "pai", scopes: VIEWER, sessionOnly: true },
  { key: "paginas", label: "Páginas", group: "nav", scopes: VIEWER },
  { key: "canais", label: "Canais", group: "cor", scopes: VIEWER },
  { key: "camadas", label: "Camadas", group: "cor", scopes: VIEWER },
  { key: "cobertura", label: "Filtro de cobertura", group: "cor", scopes: VIEWER },
  { key: "painel_codigos", label: "Painel de códigos", group: "lei", scopes: VIEWER },
  { key: "informacoes", label: "Informações", group: "pai", scopes: VIEWER },
  { key: "baixar", label: "Baixar original", group: "pai", scopes: VIEWER },
  { key: "relatorio", label: "Exportar relatório", group: "com", scopes: VIEWER },
  { key: "lado", label: "Lado a lado", group: "cmp", scopes: COMPARE },
  { key: "cortina", label: "Cortina A B", group: "cmp", scopes: COMPARE },
  { key: "piscar", label: "Piscar", group: "cmp", scopes: COMPARE },
  { key: "delta", label: "Delta", group: "cmp", scopes: COMPARE },
  { key: "alinhar_auto", label: "Alinhar automaticamente", group: "ali", scopes: COMPARE },
  { key: "por_objeto", label: "Por objeto", group: "ali", scopes: COMPARE },
  { key: "referencia", label: "Alinhar pela referência", group: "ali", scopes: COMPARE },
  { key: "diferencas", label: "Diferenças encontradas", group: "cmp", scopes: COMPARE },
  { key: "identificar", label: "Identificar diferenças", group: "cmp", scopes: COMPARE },
  { key: "foco", label: "Modo Foco", group: "cmp", scopes: COMPARE },
  { key: "molduras", label: "Molduras A e B", group: "cmp", scopes: COMPARE },
  { key: "perfil", label: "Perfil de comparação", group: "cmp", scopes: COMPARE, sessionOnly: true },
  { key: "exportar_vis", label: "Exportar visualização", group: "cmp", scopes: COMPARE, sessionOnly: true },
  { key: "trocar_ab", label: "Trocar A e B", group: "cmp", scopes: AMBOS },
] as const satisfies readonly UsageFeatureDef[];

export type UsageFeatureKey = (typeof USAGE_FEATURES)[number]["key"];

const POR_CHAVE = new Map<string, UsageFeatureDef>(USAGE_FEATURES.map((def) => [def.key, def]));

export function usageFeature(key: string): UsageFeatureDef | undefined {
  return POR_CHAVE.get(key);
}

/** A ferramenta da barra ligada → o recurso. As que não estão aqui contam no próprio botão. */
export const USAGE_OF_ACTIVE_TOOL: Partial<Record<ActiveTool, UsageFeatureKey>> = {
  pan: "mao",
  zoom_area: "zoom",
  ruler: "regua",
  area: "area",
  picker: "conta_gotas",
  barcode: "ler_codigo",
  braille: "ler_braile",
  ocr: "ler_texto",
  table_ocr: "ler_tabela",
  notes: "comentarios",
  screen_measure: "reticula",
  text_measure: "medir_texto",
};

/** O botão travado (o convite do Pro) → o recurso. */
export const USAGE_OF_LOCKED_TOOL: Record<ProInviteTool, UsageFeatureKey> = {
  braille: "ler_braile",
  ocr: "ler_texto",
  barcode_auto: "painel_codigos",
  notes_report: "relatorio",
  alignment_auto: "alinhar_auto",
  differences: "diferencas",
  text_measure: "medir_texto",
  compare_frames: "molduras",
};

export const SURFACE_LABELS: Record<UsageSurface, string> = {
  gerenciador: "Gerenciador",
  link_viewer: "Link do viewer",
  link_compare: "Link de comparação",
  link_exportado: "Comparação exportada",
};

export const PLAN_LABELS: Record<Exclude<UsagePlan, "">, string> = {
  starter: "Starter",
  pro: "Pro",
  enterprise: "Enterprise",
  /** O gerenciador e o link exportado (22/10/2026): é da equipe, não plano de cliente. */
  sessao: "Uso interno",
};

/** As superfícies que são da equipe: só aparecem com «Incluir uso interno». */
export const INTERNAL_SURFACES: readonly UsageSurface[] = ["gerenciador", "link_exportado"];

const ORDEM_DO_PLANO: Record<PlanName, number> = { starter: 0, pro: 1, enterprise: 2 };

// ---------------------------------------------------------------------------
// As faixas do calor
// ---------------------------------------------------------------------------

/** Os limites das faixas em «% das aberturas»: 0, <2, 2–5, 5–10, 10–20, 20–35, 35–50, ≥50. */
export const PCT_THRESHOLDS = [0.02, 0.05, 0.1, 0.2, 0.35, 0.5] as const;

/** Em cliques, os limites acompanham o maior valor do recorte. */
export function clickThresholds(max: number): number[] {
  const topo = Math.max(1, max);
  return [0.02, 0.05, 0.1, 0.2, 0.35, 0.6].map((fracao) => fracao * topo);
}

/** A faixa de 0 (nada) a 7 (o mais quente). */
export function heatBin(value: number, thresholds: readonly number[]): number {
  if (!(value > 0)) return 0;
  let faixa = 1;
  for (const limite of thresholds) if (value >= limite) faixa += 1;
  return faixa;
}

// ---------------------------------------------------------------------------
// As contas da tela
// ---------------------------------------------------------------------------

/** As aberturas em que o recurso existe: as do escopo dele (viewer, comparação ou os dois). */
export function scopesOf(key: string): readonly UsageScope[] {
  return usageFeature(key)?.scopes ?? AMBOS;
}

function soma(valores: readonly number[]): number {
  let total = 0;
  for (const valor of valores) total += valor;
  return total;
}

export function openingsByScope(panel: UsagePanel): Record<UsageScope, number> {
  return { viewer: soma(panel.openings.by_day.viewer), compare: soma(panel.openings.by_day.compare) };
}

export interface UsageRow {
  key: string;
  def: UsageFeatureDef;
  /** Aberturas que usaram. */
  used: number;
  /** As aberturas do escopo do recurso (o denominador). */
  base: number;
  pct: number;
  clicks: number;
  lockedClicks: number;
  /** % por dia, para a linha de tendência. */
  daily: number[];
}

function serieDoRecurso(feature: UsagePanelFeature | undefined, scopes: readonly UsageScope[], n: number) {
  const usaram = new Array<number>(n).fill(0);
  const cliques = new Array<number>(n).fill(0);
  const travados = new Array<number>(n).fill(0);
  if (!feature) return { usaram, cliques, travados };
  for (const escopo of scopes) {
    const serie = feature.days[escopo];
    if (!serie) continue;
    for (let i = 0; i < n; i += 1) {
      usaram[i] += serie.openings[i] ?? 0;
      cliques[i] += serie.clicks[i] ?? 0;
      travados[i] += serie.locked_clicks[i] ?? 0;
    }
  }
  return { usaram, cliques, travados };
}

function baseDiaria(panel: UsagePanel, scopes: readonly UsageScope[]): number[] {
  const n = panel.days.length;
  const base = new Array<number>(n).fill(0);
  for (const escopo of scopes) {
    const serie = panel.openings.by_day[escopo] ?? [];
    for (let i = 0; i < n; i += 1) base[i] += serie[i] ?? 0;
  }
  return base;
}

/**
 * O recurso existe no recorte? O que é só do gerenciador some quando o recorte
 * não tem gerenciador — e sem o uso interno, que é onde o gerenciador mora.
 */
function existeNoRecorte(def: UsageFeatureDef, surface: UsageSurface | "", includeInternal: boolean) {
  if (def.sessionOnly && (!includeInternal || (surface && surface !== "gerenciador"))) return false;
  return true;
}

/**
 * Uma linha por recurso que existe no recorte — com uso, sem uso ou só travado.
 * A ordem é a do catálogo; quem ordena é a tela.
 */
export function usageRows(panel: UsagePanel, surface: UsageSurface | "" = "", includeInternal = true): UsageRow[] {
  const n = panel.days.length;
  const porChave = new Map(panel.features.map((feature) => [feature.feature, feature]));
  const linhas: UsageRow[] = [];
  for (const def of USAGE_FEATURES as readonly UsageFeatureDef[]) {
    if (!existeNoRecorte(def, surface, includeInternal)) continue;
    const base = baseDiaria(panel, def.scopes);
    const totalBase = soma(base);
    if (totalBase <= 0) continue;
    const { usaram, cliques, travados } = serieDoRecurso(porChave.get(def.key), def.scopes, n);
    const used = soma(usaram);
    linhas.push({
      key: def.key,
      def,
      used,
      base: totalBase,
      pct: used / totalBase,
      clicks: soma(cliques),
      lockedClicks: soma(travados),
      daily: base.map((b, i) => (b > 0 ? usaram[i] / b : 0)),
    });
  }
  return linhas;
}

export interface DayColumn {
  /** Índices em `panel.days`. */
  indexes: number[];
  label: string;
  title: string;
  weekend: boolean;
}

const DIAS_DA_SEMANA = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

function partes(iso: string): { dia: number; mes: number; semana: number } {
  const [ano, mes, dia] = iso.split("-").map(Number);
  return { dia, mes, semana: new Date(Date.UTC(ano, mes - 1, dia)).getUTCDay() };
}

const ddmm = (iso: string) => {
  const { dia, mes } = partes(iso);
  return `${String(dia).padStart(2, "0")}/${String(mes).padStart(2, "0")}`;
};

/** Até 31 dias, uma coluna por dia; acima, uma por semana, a última terminando hoje. */
export function dayColumns(days: readonly string[]): DayColumn[] {
  if (days.length <= 31) {
    return days.map((iso, indice) => {
      const { dia, semana } = partes(iso);
      return {
        indexes: [indice],
        label: String(dia),
        title: `${DIAS_DA_SEMANA[semana]} ${ddmm(iso)}`,
        weekend: semana === 0 || semana === 6,
      };
    });
  }
  const colunas: DayColumn[] = [];
  for (let fim = days.length - 1; fim >= 0; fim -= 7) {
    const inicio = Math.max(0, fim - 6);
    const indexes = Array.from({ length: fim - inicio + 1 }, (_, i) => inicio + i);
    colunas.unshift({
      indexes,
      label: ddmm(days[inicio]),
      title: `semana de ${ddmm(days[inicio])} a ${ddmm(days[fim])}`,
      weekend: false,
    });
  }
  return colunas;
}

export interface CellValue {
  used: number;
  base: number;
  clicks: number;
  pct: number;
}

/** O valor de uma linha numa coluna de dias (soma das aberturas e dos cliques). */
export function dayCell(panel: UsagePanel, row: UsageRow, column: DayColumn): CellValue {
  const feature = panel.features.find((item) => item.feature === row.key);
  let used = 0;
  let clicks = 0;
  let base = 0;
  for (const indice of column.indexes) {
    for (const escopo of row.def.scopes) {
      base += panel.openings.by_day[escopo]?.[indice] ?? 0;
      used += feature?.days[escopo]?.openings[indice] ?? 0;
      clicks += feature?.days[escopo]?.clicks[indice] ?? 0;
    }
  }
  return { used, base, clicks, pct: base > 0 ? used / base : 0 };
}

export interface OrgColumn {
  id: string;
  name: string;
  /** As aberturas da empresa por escopo. */
  openings: Record<UsageScope, number>;
  /** O plano mais alto das aberturas dela no período (o teto do que ela pode usar). */
  topPlan: PlanName | null;
  team: boolean;
}

/**
 * As colunas da aba «Por organização»: as empresas por aberturas, depois «Sem
 * organização» e «Equipe (gerenciador)». `limit` corta as empresas — com
 * centenas, a tela mostra as que mais abriram.
 */
export function orgColumns(panel: UsagePanel, limit = 24): { columns: OrgColumn[]; hidden: number } {
  const nomes = new Map(panel.organizations.map((org) => [org.id, org.name]));
  const porEmpresa = new Map<string, OrgColumn>();
  for (const linha of panel.openings.by_org) {
    const equipe = linha.plan === "sessao";
    const id = equipe ? "__equipe__" : linha.organization_id;
    const coluna =
      porEmpresa.get(id) ??
      ({
        id,
        name: equipe ? "Uso interno" : id ? nomes.get(id) || id : "Sem organização",
        openings: { viewer: 0, compare: 0 },
        topPlan: null,
        team: equipe,
      } satisfies OrgColumn);
    coluna.openings[linha.scope] += linha.openings;
    if (!equipe && linha.plan && linha.plan !== "sessao" && linha.openings > 0) {
      const plano = linha.plan as PlanName;
      if (coluna.topPlan === null || ORDEM_DO_PLANO[plano] > ORDEM_DO_PLANO[coluna.topPlan]) coluna.topPlan = plano;
    }
    porEmpresa.set(id, coluna);
  }
  const total = (coluna: OrgColumn) => coluna.openings.viewer + coluna.openings.compare;
  const empresas = [...porEmpresa.values()].filter((coluna) => coluna.id && !coluna.team).sort((a, b) => total(b) - total(a));
  // «Sem organização» antes da «Equipe».
  const resto = [...porEmpresa.values()].filter((coluna) => !coluna.id || coluna.team).sort((a, b) => Number(a.team) - Number(b.team));
  return { columns: [...empresas.slice(0, limit), ...resto], hidden: Math.max(0, empresas.length - limit) };
}

export type OrgCell =
  | { kind: "absent" }
  | { kind: "locked"; requiredPlan: PlanName; lockedClicks: number }
  | ({ kind: "value" } & CellValue);

/** As linhas `by_org` de uma coluna: a empresa, «sem organização» ou a equipe (plano `sessao`). */
function daColuna(linha: { organization_id: string; plan: UsagePlan }, column: OrgColumn): boolean {
  if (column.team) return linha.plan === "sessao";
  return linha.organization_id === column.id && linha.plan !== "sessao";
}

/**
 * A célula empresa × recurso. «Travado» quando o recurso tem plano mínimo acima
 * do maior plano da empresa no período e ela não o usou; «não existe» quando a
 * empresa não tem aberturas do escopo dele, ou é recurso só do gerenciador.
 */
export function orgCell(panel: UsagePanel, row: UsageRow, column: OrgColumn): OrgCell {
  const base = row.def.scopes.reduce((acc, escopo) => acc + column.openings[escopo], 0);
  if (base <= 0) return { kind: "absent" };
  if (row.def.sessionOnly && !column.team) return { kind: "absent" };
  const feature = panel.features.find((item) => item.feature === row.key);
  let used = 0;
  let clicks = 0;
  let locked = 0;
  for (const linha of feature?.by_org ?? []) {
    if (!daColuna(linha, column) || !row.def.scopes.includes(linha.scope)) continue;
    used += linha.openings;
    clicks += linha.clicks;
    locked += linha.locked_clicks;
  }
  const minimo = usageMinPlan(panel, row.key);
  if (!column.team && minimo && column.topPlan && ORDEM_DO_PLANO[minimo] > ORDEM_DO_PLANO[column.topPlan] && used === 0) {
    return { kind: "locked", requiredPlan: minimo, lockedClicks: locked };
  }
  return { kind: "value", used, base, clicks, pct: used / base };
}

export function usageMinPlan(panel: UsagePanel, key: string): PlanName | null {
  return panel.catalog.find((item) => item.feature === key)?.min_plan ?? null;
}

/** Quanto do uso (aberturas que usaram) veio de cada recorte, com o denominador do recorte. */
export function usageBreakdown(
  panel: UsagePanel,
  row: UsageRow,
  dimension: "surface" | "plan",
): Array<{ key: string; used: number; base: number; pct: number }> {
  const feature = panel.features.find((item) => item.feature === row.key);
  const usaram = new Map<string, number>();
  const bases = new Map<string, number>();
  for (const corte of panel.openings.by_cut) {
    if (!row.def.scopes.includes(corte.scope)) continue;
    const chave = corte[dimension];
    bases.set(chave, (bases.get(chave) ?? 0) + corte.openings);
  }
  for (const corte of feature?.by_cut ?? []) {
    if (!row.def.scopes.includes(corte.scope)) continue;
    const chave = corte[dimension];
    usaram.set(chave, (usaram.get(chave) ?? 0) + corte.openings);
  }
  return [...bases.entries()]
    .filter(([, base]) => base > 0)
    .map(([key, base]) => ({ key, used: usaram.get(key) ?? 0, base, pct: (usaram.get(key) ?? 0) / base }));
}

// ---------------------------------------------------------------------------
// O pacote do navegador
// ---------------------------------------------------------------------------

/**
 * Os cliques acumulados até o próximo envio. Um recurso vira uma entrada só,
 * com os cliques somados — o servidor conta «usou» uma vez por abertura.
 */
export class UsageBuffer {
  private pendentes = new Map<string, { clicks: number; locked: number }>();

  add(feature: string, locked = false): void {
    const atual = this.pendentes.get(feature) ?? { clicks: 0, locked: 0 };
    if (locked) atual.locked += 1;
    else atual.clicks += 1;
    this.pendentes.set(feature, atual);
  }

  get size(): number {
    return this.pendentes.size;
  }

  /** Esvazia e devolve o pacote, na ordem do primeiro clique de cada recurso. */
  drain(): Array<{ feature: string; clicks: number; locked_clicks: number }> {
    const pacote = [...this.pendentes].map(([feature, contagem]) => ({
      feature,
      clicks: contagem.clicks,
      locked_clicks: contagem.locked,
    }));
    this.pendentes = new Map();
    return pacote;
  }
}

// ---------------------------------------------------------------------------
// As cores do calor (um matiz só, do quase nada ao mais usado)
// ---------------------------------------------------------------------------

/** No fundo escuro do gerenciador: o pouco recua para o fundo, o muito acende. */
export const HEAT_DARK = ["#1a2030", "#16325e", "#1c4a8a", "#2561b3", "#2f7ad6", "#5598e7", "#86b6ef", "#cde2fb"] as const;
/** Na barra clara do viewer: o pouco é quase branco, o muito é azul-escuro. */
export const HEAT_LIGHT = ["#eef1f6", "#e3eefc", "#cde2fb", "#9ec5f4", "#6da7ec", "#3987e5", "#256abf", "#184f95"] as const;

/** A cor do texto sobre a faixa: clara no escuro, escura no claro. */
export function heatInk(bin: number, theme: "dark" | "light"): string {
  if (theme === "dark") return bin >= 5 ? "#0f1320" : "#e4e9ef";
  return bin >= 4 ? "#ffffff" : "#111827";
}

/** As aberturas que ligaram algum recurso (a linha `*` do servidor): o «Só olharam» é o resto. */
export function openingsThatUsedSomething(panel: UsagePanel, scopes: readonly UsageScope[] = AMBOS): number {
  const algum = panel.features.find((feature) => feature.feature === "*");
  if (!algum) return 0;
  let total = 0;
  for (const escopo of scopes) total += soma(algum.days[escopo]?.openings ?? []);
  return total;
}
